import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import ACCESS, { Gates, DriftDevIntegrationGates } from 'utils/Access'
import { Integrations as IntegrationsList, IntegrationCategories } from './ListConstants'
import IntegrationCard from './components/IntegrationCard'
import style from 'styles/components/integrations/IntegrationList.styl'
import Fuse from 'fuse.js'
import { ensureIsAdmin } from 'app/modules/access/helpers'
import { fetchInstalledApps } from 'app/modules/integrations/api'
import { mergeAll, sortBy, compose, toLower, prop, isEmpty, toPairs } from 'ramda'
import notFoundIconSrc from 'images/svg/empty-integrations.svg'
import ConditionalRender from 'components/shared/ConditionalRender'
import Select from 'react-select-plus'
import SuggestIntegration from './components/SuggestIntegration'
import SuggestIntegrationModal from './components/SuggestIntegrationModal'
import { ELOQUA_APP_ID, PARDOT_APP_ID, DRIFT_DEV_INTEGRATIONS } from 'constants/ResourcesConstants'
import { Button, H3 } from '@driftt/tide-core-legacy'
import { createAppName } from 'components/Integration/helpers'

import { fetchIntegrationStates } from 'app/modules/integrations/actions'
import { connectionsRoot } from 'app/modules/integrations/selectors'

const sortByNameCaseInsensitive = sortBy(compose(toLower, prop('title')))

const isIntegrationVisible = (integration) => integration.renderGate
  ? ACCESS.hasGates(integration.renderGate)
  : true

const createCustomIntegration = (app, requiredGates, humanReadableId) => {
  const id = humanReadableId || app.id

  return {
    id: id,
    title: app.name,
    keywords: 'installed',
    description: app.shortDescription,
    logoSrc: app.iconUrl,
    path: `/settings/setup/${id}`,
    installed: app.installed,
    requiredGates: requiredGates,
    isGatedByPlan: true,
    upgradeRibbonText: 'Upgrade'
  }
}

const FUSE_CONFIG = {
  keys: ['title', 'description', 'keywords', 'upgradeRibbonText'],
  threshold: 0.2
}

const NoResultsView = ({onClick}) => (
  <div className="integrations-empty-view">
    <img
      className="integrations-empty-view-icon"
      src={notFoundIconSrc}
    />
    <span className="integrations-empty-view-title">
      Whoops, we can't find the integration!
    </span>
    <p className="integrations-empty-view-description">
      Looking for an integration we don't have? Let our team know!
    </p>
    <Button className="integrations-suggestion-button" onClick={onClick}>Suggest an integration</Button>
  </div>
)


class IntegrationCardGroup extends Component {
  render() {
    const { integrations, integrationStates } = this.props
    const visibleIntegrations = integrations.filter(isIntegrationVisible)

    return (
      <div className={style.IntegrationList}>
        {_.map(visibleIntegrations, integration => (
          <IntegrationCard
            key={integration.id}
            {...integration}
            hasAccess={
              (_.has(integration, 'grandfathered') ? integration.grandfathered() : false) ||
                (_.has(integration, 'requiredGates') ? ACCESS.hasGates(integration.requiredGates) : true)
            }
            isActive={integrationStates[integration.id] || false}
          />
        ))}
      </div>
    )
  }
}

class IntegrationList extends Component {
  constructor(props) {
    super(props)
    this.state = {
      filteredIntegrations: IntegrationsList,
      selectedIntegrationCategory: 'ALL',
      selectedIntegrationPlan: 'ALL',
      query: '',
      availableIntegrations: [],
      gatedIntegrations: [],
      isPaidPlan: ACCESS.hasGates([Gates.PREMIUM, Gates.PREMIUM_TEAM]),
      externalIntegrations: [],
      openSuggestIntegrationModal:false,
      publicExternalIntegrations: []
    }
  }

  static propTypes = {
    integrationStates: PropTypes.object.isRequired,
  }

  componentDidMount() {
    this.sortIntegrationsByGatedStatus()
    this.fetchExternalIntegrations()

    if (isEmpty(this.props.integrationStates)){
      this.props.actions.fetchIntegrationStates()
    }
  }

  fetchExternalIntegrations = () => {
    let isExternalIntegration = false
    const mapToCard = (app) => {
      if (DRIFT_DEV_INTEGRATIONS.has(app.name)){
        return createCustomIntegration(app, [DriftDevIntegrationGates[app.id]], app.name.toLowerCase())
      } else if (app.id.length === 36) {
        isExternalIntegration = true
      }

      const isIntegrationInList = (app) => {
        return _.find(this.state.publicExternalIntegrations, (integration) => app.id === integration.id) !== undefined
      }

      const getExternalAppPath = (app) => {
        if (isIntegrationInList(app)){
          return createAppName(app.name)
        }
        return app.id
      }

      const getExternalAppName = (app) => {
        let publicExternalIntegrationsList = this.state.publicExternalIntegrations
        if (app.distributionStatus === "PUBLIC" && !isIntegrationInList(app)){
          publicExternalIntegrationsList.push(app)
          this.setState({ publicExternalIntegrations: publicExternalIntegrationsList })
          return createAppName(app.name)
        }
      }

      return {
        id: isExternalIntegration ? getExternalAppName(app) : app.id,
        title: app.name,
        keywords: 'installed',
        description: app.shortDescription,
        logoSrc: app.iconUrl,
        path: `/settings/setup/${isExternalIntegration ? getExternalAppPath(app) : app.id}`,
        installed: app.installed
      }
    }

    const onFetched = (apps) => {
      const externalIntegrations = apps.map(mapToCard)
      this.setState({
        filteredIntegrations: [...IntegrationsList, ...externalIntegrations],
        externalIntegrations
      })
    }

    fetchInstalledApps().then(onFetched)
  }

  componentWillReceiveProps() {
    this.sortIntegrationsByGatedStatus()
  }

  sortIntegrationsByGatedStatus() {
    let { isPaidPlan, filteredIntegrations } = this.state
    
    // put them all in one bucket if not free plan
    if (isPaidPlan) {
      this.setState({
        availableIntegrations: filteredIntegrations,
      })
      return
    }

    const newState = _.reduce(
      filteredIntegrations,
      (sorted, integration) => {
        if (_.get(integration, 'requiredGates', false)) {
          if (ACCESS.hasGates(integration.requiredGates)) {
            sorted.availableIntegrations.push(integration)
          } else {
            sorted.gatedIntegrations.push(integration)
          }
        } else {
          sorted.availableIntegrations.push(integration)
        }
        return sorted
      },
      {
        availableIntegrations: [],
        gatedIntegrations: [],
      },
    )
    this.setState(newState)
  }

  handleIntegrationCategorySelection = ({ value }) => {
    const { keywords } = IntegrationCategories[value]
    this.updateResults(keywords)
    this.setState({
      selectedIntegrationCategory: value,
      selectedIntegrationPlan: 'ALL',
      query: ''
    })
  }

  handleSearchInputChange = ({ target }) => {
    this.updateResults(target.value)
    this.setState({
      selectedIntegrationCategory: 'ALL',
      selectedIntegrationPlan: 'ALL',
      query: target.value
    })
  }
  
  searchIntegrations = (searchTerm, integrations) => {
    const { externalIntegrations } = this.state
    if (searchTerm === 'installed') {
      const { integrationStates } = this.props
      return [
        ...integrations.filter(integration => integrationStates[integration.id]),
        ...externalIntegrations.filter(integration => integration.installed)
      ]
    } else if (searchTerm === 'partner') {
      // Return non-drift made third party applications.
      return externalIntegrations.filter(integration => !DRIFT_DEV_INTEGRATIONS.has(integration.title))
    }

    const fuse = new Fuse(integrations, FUSE_CONFIG)
    return fuse.search(searchTerm)
  }

  updateResults = updatedQuery => {
    const { externalIntegrations } = this.state
    const allIntegrations = [...IntegrationsList, ...externalIntegrations]

    const results = !!updatedQuery
      ? this.searchIntegrations(updatedQuery, allIntegrations)
      : allIntegrations

    this.setState({ filteredIntegrations: results })
  }

  resolveCanRenderBuildSection = () => {
    const { query, selectedIntegrationCategory, selectedIntegrationPlan } = this.state
    return !query && selectedIntegrationCategory === 'ALL' && selectedIntegrationPlan === 'ALL'
  }

  renderIntegrations() {
    const { externalIntegrations, filteredIntegrations } = this.state
    const { integrationStates } = this.props
    if (integrationStates) {
      integrationStates['clearbit'] = !!integrationStates['clearbit_reveal'] || !!integrationStates['clearbit_enrichment']
    }
    const integrations = sortByNameCaseInsensitive([...filteredIntegrations])
    const externalIntegrationStates = externalIntegrations.map(integration => ({ [integration.id]: integration.installed }))
    const allIntegrationStates = { ...integrationStates, ...mergeAll(externalIntegrationStates) }
    const canRenderBuildSection = this.resolveCanRenderBuildSection()

    return (
      <div>
        <ConditionalRender condition={!isEmpty(integrations)}>
          <IntegrationCardGroup integrations={integrations} integrationStates={allIntegrationStates} />
          <ConditionalRender condition={canRenderBuildSection}>
            <SuggestIntegration onClick={this.openModal}/>
          </ConditionalRender>
        </ConditionalRender>
        <ConditionalRender condition={isEmpty(integrations)}>
          <NoResultsView onClick={this.openModal}/>
        </ConditionalRender>
      </div>
    )
  }
  openModal = () => {
    this.setState({openSuggestIntegrationModal: true})
  }
  closeModal = () => {
    this.setState({openSuggestIntegrationModal: false})
  }

  renderCategoryDropDown() {
    const { selectedIntegrationCategory } = this.state
    const options = toPairs(IntegrationCategories)
      .map(pair => ({ value: pair[0], label: pair[1].label }))

    return (
      <Select
        className="integrations-list-select integrations-list-select-categories"
        onChange={this.handleIntegrationCategorySelection}
        options={options}
        value={selectedIntegrationCategory}
        clearable={false}
        searchable={false}
        autosize={true}
        autofocus={false}
      />
    )
  }

  render() {
    return (
      <div className="integration-list-container">
        <H3 className="integrations-header-title-description">Integrations</H3>
        <div className="integrations-header">
          <div className="drift-form integrations-form">
            <input
              type="text"
              placeholder="Search"
              value={this.state.query}
              onChange={this.handleSearchInputChange}
            />
          </div>
          {this.renderCategoryDropDown()}
        </div>
        {this.renderIntegrations()}
        <SuggestIntegrationModal open={this.state.openSuggestIntegrationModal} size="small" onClose={this.closeModal}/>
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  integrationStates: connectionsRoot(state)
})

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchIntegrationStates
    },
    dispatch
  )
})

export default ensureIsAdmin(connect(mapStateToProps, mapDispatchToProps)(IntegrationList))



// WEBPACK FOOTER //
// ./src/scripts/components/Integration/List.js